import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../components/ToastContainer';
import {
  getAdminNodes,
  getPendingNodes,
  approveNode,
  rejectNode,
  toggleNode,
  toggleNodeVisibility,
  updatePendingNode,
  updateNodeSettings,
  deleteNode,
  getNodeLicenses,
  getLocations,
  createLocation,
  updateLocation,
  deleteLocation,
  getAgentRegisterKeys,
  createAgentRegisterKey,
  revokeAgentRegisterKey,
  restartNode,
  listEdgeHosts,
  createEdgeHost,
  redeployEdgeHost
} from '../../services/api';
import '../../styles/admin-ui.css';
import './Servers.css';

const LICENSE_TYPES = ['free', 'basic', 'pro', 'enterprise'];

const COUNTRY_FLAGS = {
  TR: '🇹🇷', US: '🇺🇸', DE: '🇩🇪', GB: '🇬🇧', FR: '🇫🇷',
  NL: '🇳🇱', JP: '🇯🇵', SG: '🇸🇬', AU: '🇦🇺', CA: '🇨🇦',
  IT: '🇮🇹', ES: '🇪🇸', SE: '🇸🇪', CH: '🇨🇭', AT: '🇦🇹',
  BE: '🇧🇪', PL: '🇵🇱', CZ: '🇨🇿', RO: '🇷🇴', HU: '🇭🇺',
  GR: '🇬🇷', PT: '🇵🇹', NO: '🇳🇴', DK: '🇩🇰', FI: '🇫🇮',
  IE: '🇮🇪', BR: '🇧🇷', MX: '🇲🇽', AR: '🇦🇷', IN: '🇮🇳',
  KR: '🇰🇷', HK: '🇭🇰', TW: '🇹🇼', RU: '🇷🇺', UA: '🇺🇦',
  IL: '🇮🇱', ZA: '🇿🇦', AE: '🇦🇪', SA: '🇸🇦', NZ: '🇳🇿'
};

const POPULAR_FLAGS = [
  '🇹🇷', '🇺🇸', '🇩🇪', '🇬🇧', '🇫🇷', '🇳🇱', '🇯🇵', '🇸🇬', '🇦🇺', '🇨🇦',
  '🇮🇹', '🇪🇸', '🇸🇪', '🇨🇭', '🇦🇹', '🇧🇪', '🇵🇱', '🇧🇷', '🇰🇷', '🇷🇺'
];

const normalizeLicenseType = (type) => (type === 'premium' ? 'pro' : type);

const Servers = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showError, showSuccess } = useToast();
  const [servers, setServers] = useState([]);
  const [pendingNodes, setPendingNodes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [registerKeys, setRegisterKeys] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showRegisterKeysModal, setShowRegisterKeysModal] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [edgeHosts, setEdgeHosts] = useState([]);
  const [deploying, setDeploying] = useState(false);
  const [showFlagPicker, setShowFlagPicker] = useState(false);
  const [showRevokeConfirmModal, setShowRevokeConfirmModal] = useState(false);
  const [showDeleteServerModal, setShowDeleteServerModal] = useState(false);
  const [showEditPendingModal, setShowEditPendingModal] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [keyToRevoke, setKeyToRevoke] = useState(null);
  const [pendingModalMessage, setPendingModalMessage] = useState(null);
  const [nodeToApprove, setNodeToApprove] = useState(null);

  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const [locationForm, setLocationForm] = useState({
    country_name: '',
    country_code: '',
    city: '',
    flag_emoji: ''
  });
  const [editPendingForm, setEditPendingForm] = useState({
    name: '',
    location_id: null
  });
  const [settingsForm, setSettingsForm] = useState({
    name: '',
    location_id: 0,
    license_types: [],
    fallback_node_id: 0,
    clear_fallback: false
  });
  const [newKeyForm, setNewKeyForm] = useState({
    description: '',
    expires_in_days: 30
  });
  const [deployForm, setDeployForm] = useState({
    description: '',
    ssh_host: '',
    ssh_port: 22,
    ssh_user: 'root',
    ssh_password: '',
    ssh_private_key: '',
    expires_in_days: 30,
    deploy_now: true
  });
  const [approveFallbackId, setApproveFallbackId] = useState(0);

  const [keySearchQuery, setKeySearchQuery] = useState('');
  const [keySortOrder, setKeySortOrder] = useState('date_desc');
  const [keyCurrentPage, setKeyCurrentPage] = useState(1);
  const keyPageSize = 10;

  useEffect(() => {
    fetchServers();
    fetchPendingNodes();
    fetchLocations();
    fetchEdgeHosts();
  }, []);

  const fetchServers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAdminNodes();
      const nodesArray = Array.isArray(data) ? data : (data.nodes || []);
      const formattedServers = nodesArray.map(node => {
        const live = !!node.agent_live;
        const heartbeatFresh =
          node.last_heartbeat &&
          Date.now() - new Date(node.last_heartbeat).getTime() < 2 * 60 * 1000;
        const status =
          live || (node.status === 'online' && heartbeatFresh)
            ? 'online'
            : node.status === 'maintenance'
              ? 'maintenance'
              : 'offline';
        return {
          id: node.id,
          name: node.name,
          country: node.loc_country || node.location || 'Unknown',
          countryCode: node.loc_country_code || node.country_code,
          flagEmoji: node.loc_flag || node.flag_emoji || '🌐',
          city: node.loc_city || node.location || 'Unknown',
          ip: node.ip_address || '-',
          port: node.endpoint_port || node.port || 51820,
          status,
          agentStatus: live ? 'connected' : (node.agent_status || 'disconnected'),
          agentLive: live,
          isApproved: node.is_approved,
          isVisible: node.is_visible || false,
          locationId: node.location_id,
          load: (live || status === 'online') ? (node.load_percent || 0) : 0,
          users: (live || status === 'online') ? (node.current_users || 0) : 0,
          capacity: node.capacity || 500,
          publicKey: node.public_key,
          lastHeartbeat: node.last_heartbeat,
          fallbackNodeId: node.fallback_node_id || null
        };
      });
      setServers(formattedServers);
    } catch (err) {
      console.error('Failed to fetch servers:', err);
      setError(t('systemAdmin.servers.error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingNodes = async () => {
    try {
      const data = await getPendingNodes();
      const nodesArray = Array.isArray(data) ? data : (data.nodes || []);
      setPendingNodes(nodesArray);
    } catch (err) {
      console.error('Failed to fetch pending nodes:', err);
    }
  };

  const fetchLocations = async () => {
    try {
      const data = await getLocations();
      setLocations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch locations:', err);
    }
  };

  const fetchEdgeHosts = async () => {
    try {
      const data = await listEdgeHosts();
      setEdgeHosts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch edge hosts:', err);
    }
  };

  const handleOpenDeployModal = () => {
    setDeployForm({
      description: '',
      ssh_host: '',
      ssh_port: 22,
      ssh_user: 'root',
      ssh_password: '',
      ssh_private_key: '',
      expires_in_days: 30,
      deploy_now: true
    });
    setShowDeployModal(true);
    fetchEdgeHosts();
  };

  const handleDeployServer = async () => {
    if (!deployForm.ssh_host.trim()) {
      showError('SSH host is required');
      return;
    }
    if (!deployForm.ssh_password.trim() && !deployForm.ssh_private_key.trim()) {
      showError('Provide SSH password or private key');
      return;
    }
    try {
      setDeploying(true);
      await createEdgeHost({
        description: deployForm.description || deployForm.ssh_host,
        ssh_host: deployForm.ssh_host.trim(),
        ssh_port: deployForm.ssh_port || 22,
        ssh_user: deployForm.ssh_user || 'root',
        ssh_password: deployForm.ssh_password || undefined,
        ssh_private_key: deployForm.ssh_private_key || undefined,
        deploy_now: !!deployForm.deploy_now,
        expires_in_days: deployForm.expires_in_days || 30
      });
      showSuccess('Deploy started — agent will appear under Pending when registered');
      fetchEdgeHosts();
      fetchPendingNodes();
      setTimeout(() => {
        fetchEdgeHosts();
        fetchPendingNodes();
        fetchServers();
      }, 8000);
    } catch (err) {
      console.error('Deploy failed:', err);
      showError(err.message || 'Deploy failed');
    } finally {
      setDeploying(false);
    }
  };

  const handleRedeployHost = async (hostId) => {
    try {
      await redeployEdgeHost(hostId, 30);
      showSuccess('Redeploy started');
      fetchEdgeHosts();
    } catch (err) {
      showError(err.message || 'Redeploy failed');
    }
  };

  const handleRestartNode = async (server) => {
    try {
      await restartNode(server.id);
      showSuccess(`Restart sent to ${server.name}`);
    } catch (err) {
      showError(err.message || 'Restart failed (agent offline?)');
    }
  };

  const handleApproveNode = async (nodeId) => {
    const node = pendingNodes.find(n => n.id === nodeId) || servers.find(s => s.id === nodeId);
    if (node) {
      setNodeToApprove(node);
      setEditPendingForm({
        name: node.name || '',
        location_id: node.location_id || null
      });
      setShowEditPendingModal(true);
    }
  };

  const handleEditPendingSubmit = async () => {
    if (!nodeToApprove) return;

    try {
      if (editPendingForm.location_id || editPendingForm.name) {
        await updatePendingNode(nodeToApprove.id, editPendingForm);
      }

      await approveNode(nodeToApprove.id, {
        location_id: editPendingForm.location_id,
        fallback_node_id: approveFallbackId > 0 ? approveFallbackId : undefined
      });

      setPendingModalMessage({ type: 'success', text: 'Server approved successfully!' });
      setShowEditPendingModal(false);
      setNodeToApprove(null);
      setApproveFallbackId(0);
      fetchServers();
      fetchPendingNodes();
      setTimeout(() => setPendingModalMessage(null), 3000);
    } catch (err) {
      console.error('Failed to approve node:', err);
      setPendingModalMessage({ type: 'error', text: 'Failed to approve server. Please try again.' });
      setTimeout(() => setPendingModalMessage(null), 5000);
    }
  };

  const handleToggleVisibility = async (nodeId) => {
    try {
      await toggleNodeVisibility(nodeId);
      fetchServers();
      showSuccess(t('systemAdmin.servers.visibilityToggled', 'Server visibility updated'));
    } catch (err) {
      console.error('Failed to toggle visibility:', err);
      showError(t('systemAdmin.servers.visibilityError', 'Failed to toggle visibility'));
    }
  };

  const handleRejectClick = (node) => {
    setSelectedNode(node);
    setShowRejectModal(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedNode) return;
    try {
      await rejectNode(selectedNode.id);
      setShowRejectModal(false);
      setSelectedNode(null);
      setPendingModalMessage({ type: 'success', text: 'Server rejected successfully!' });
      fetchServers();
      fetchPendingNodes();
      setTimeout(() => setPendingModalMessage(null), 3000);
    } catch (err) {
      console.error('Failed to reject node:', err);
      setPendingModalMessage({ type: 'error', text: 'Failed to reject server. Please try again.' });
      setTimeout(() => setPendingModalMessage(null), 5000);
    }
  };

  const handleToggleNode = async (server) => {
    try {
      const newEnabled = server.status !== 'online';
      await toggleNode(server.id, newEnabled);
      fetchServers();
    } catch (err) {
      console.error('Failed to toggle node:', err);
      showError(t('systemAdmin.servers.toggleError', 'Sunucu durumu değiştirilirken bir hata oluştu'));
    }
  };

  const handleSettingsClick = async (server) => {
    setSelectedNode(server);
    try {
      const licenses = await getNodeLicenses(server.id);
      const normalized = (Array.isArray(licenses) ? licenses : []).map(normalizeLicenseType);
      setSettingsForm({
        name: server.name,
        location_id: server.locationId || 0,
        license_types: [...new Set(normalized)],
        fallback_node_id: server.fallbackNodeId || 0,
        clear_fallback: false
      });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Failed to fetch node licenses:', err);
      setSettingsForm({
        name: server.name,
        location_id: server.locationId || 0,
        license_types: [],
        fallback_node_id: server.fallbackNodeId || 0,
        clear_fallback: false
      });
      setShowSettingsModal(true);
    }
  };

  const handleSettingsSave = async () => {
    if (!selectedNode) return;
    try {
      const payload = {
        name: settingsForm.name,
        location_id: settingsForm.location_id,
        license_types: settingsForm.license_types.map(normalizeLicenseType),
        fallback_node_id: settingsForm.fallback_node_id || 0,
        clear_fallback: !settingsForm.fallback_node_id
      };
      await updateNodeSettings(selectedNode.id, payload);
      setShowSettingsModal(false);
      setSelectedNode(null);
      fetchServers();
    } catch (err) {
      console.error('Failed to save settings:', err);
      showError(t('systemAdmin.servers.saveError', 'Ayarlar kaydedilirken bir hata oluştu'));
    }
  };

  const handleDeleteServerClick = () => {
    setDeleteConfirmName('');
    setShowDeleteServerModal(true);
  };

  const handleDeleteServerConfirm = async () => {
    if (!selectedNode || deleteConfirmName !== selectedNode.name) return;
    try {
      await deleteNode(selectedNode.id);
      setShowDeleteServerModal(false);
      setShowSettingsModal(false);
      setSelectedNode(null);
      setDeleteConfirmName('');
      showSuccess(t('systemAdmin.servers.deleteSuccess', 'Server deleted successfully!'));
      fetchServers();
    } catch (err) {
      console.error('Failed to delete server:', err);
      showError(t('systemAdmin.servers.deleteError', 'Server silinirken bir hata oluştu'));
    }
  };

  const handleLicenseToggle = (licenseType) => {
    const type = normalizeLicenseType(licenseType);
    setSettingsForm(prev => ({
      ...prev,
      license_types: prev.license_types.includes(type)
        ? prev.license_types.filter(l => l !== type)
        : [...prev.license_types, type]
    }));
  };

  const handleAddLocation = () => {
    setSelectedLocation(null);
    setLocationForm({ country_name: '', country_code: '', city: '', flag_emoji: '' });
    setShowLocationModal(true);
  };

  const handleEditLocation = (loc) => {
    setSelectedLocation(loc);
    setLocationForm({
      country_name: loc.country_name,
      country_code: loc.country_code,
      city: loc.city,
      flag_emoji: loc.flag_emoji
    });
    setShowLocationModal(true);
  };

  const handleLocationSave = async () => {
    try {
      if (selectedLocation) {
        const result = await updateLocation(selectedLocation.id, { ...locationForm, is_active: true });
        if (result && result.success) {
          showSuccess(t('systemAdmin.servers.locationUpdateSuccess', 'Location updated successfully!'));
        }
      } else {
        const result = await createLocation(locationForm);
        if (result && result.success) {
          showSuccess(t('systemAdmin.servers.locationCreateSuccess', 'Location created successfully!'));
        }
      }
      setShowLocationModal(false);
      fetchLocations();
    } catch (err) {
      console.error('Failed to save location:', err);
      showError(t('systemAdmin.servers.locationSaveError', 'An error occurred while saving the location'));
    }
  };

  const handleCountryCodeChange = (code) => {
    const upperCode = code.toUpperCase();
    setLocationForm(prev => ({
      ...prev,
      country_code: upperCode,
      flag_emoji: COUNTRY_FLAGS[upperCode] || prev.flag_emoji
    }));
  };

  const handleFlagSelect = (flag) => {
    setLocationForm(prev => ({ ...prev, flag_emoji: flag }));
    setShowFlagPicker(false);
  };

  const fetchRegisterKeys = async () => {
    try {
      const data = await getAgentRegisterKeys();
      setRegisterKeys(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch register keys:', err);
    }
  };

  const handleOpenRegisterKeysModal = () => {
    fetchRegisterKeys();
    setShowRegisterKeysModal(true);
  };

  const handleCreateRegisterKey = async () => {
    try {
      const result = await createAgentRegisterKey(newKeyForm.description, newKeyForm.expires_in_days);
      if (result && result.register_key) {
        showSuccess(t('systemAdmin.servers.keyCreateSuccess', 'New register key created!'));
        setNewKeyForm({ description: '', expires_in_days: 30 });
        fetchRegisterKeys();
      }
    } catch (err) {
      console.error('Failed to create register key:', err);
      showError(t('systemAdmin.servers.keyCreateError', 'An error occurred while creating the register key'));
    }
  };

  const handleRevokeClick = (keyId) => {
    setKeyToRevoke(keyId);
    setShowRevokeConfirmModal(true);
  };

  const handleRevokeConfirm = async () => {
    if (!keyToRevoke) return;
    try {
      await revokeAgentRegisterKey(keyToRevoke);
      showSuccess(t('systemAdmin.servers.keyRevokeSuccess', 'Key revoked successfully!'));
      fetchRegisterKeys();
    } catch (err) {
      console.error('Failed to revoke key:', err);
      showError(t('systemAdmin.servers.keyRevokeError', 'An error occurred while revoking the key'));
    } finally {
      setShowRevokeConfirmModal(false);
      setKeyToRevoke(null);
    }
  };

  const filteredAndSortedKeys = React.useMemo(() => {
    let result = [...registerKeys];

    if (keySearchQuery) {
      const query = keySearchQuery.toLowerCase();
      result = result.filter(key =>
        key.register_key.toLowerCase().includes(query) ||
        (key.description && key.description.toLowerCase().includes(query))
      );
    }

    result.sort((a, b) => {
      switch (keySortOrder) {
        case 'date_asc':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'key_asc':
          return a.register_key.localeCompare(b.register_key);
        case 'date_desc':
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });

    return result;
  }, [registerKeys, keySearchQuery, keySortOrder]);

  const paginatedKeys = React.useMemo(() => {
    const startIndex = (keyCurrentPage - 1) * keyPageSize;
    return filteredAndSortedKeys.slice(startIndex, startIndex + keyPageSize);
  }, [filteredAndSortedKeys, keyCurrentPage]);

  const totalKeyPages = Math.ceil(filteredAndSortedKeys.length / keyPageSize);

  const handleDeleteLocation = async (locId) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;
    try {
      await deleteLocation(locId);
      showSuccess(t('systemAdmin.servers.locationDeleteSuccess', 'Location deleted successfully!'));
      fetchLocations();
    } catch (err) {
      console.error('Failed to delete location:', err);
      showError(err.message || t('systemAdmin.servers.locationDeleteError', 'An error occurred while deleting the location'));
    }
  };

  const getLoadColor = (load) => {
    if (load < 50) return '#047857';
    if (load < 75) return '#9a6700';
    return '#b42318';
  };

  const getStatusChip = (status) => {
    if (status === 'online') return { cls: 'ok', text: t('systemAdmin.servers.status.online', 'Online') };
    if (status === 'maintenance') return { cls: 'warn', text: t('systemAdmin.servers.status.maintenance', 'Maintenance') };
    return { cls: 'danger', text: t('systemAdmin.servers.status.offline', 'Offline') };
  };

  const countries = ['all', ...new Set(servers.map(s => s.country))];
  const filteredServers = selectedCountry === 'all'
    ? servers
    : servers.filter(s => s.country === selectedCountry);

  const totalServers = servers.length;
  const onlineServers = servers.filter(s => s.status === 'online').length;
  const totalUsers = servers.reduce((sum, s) => sum + s.users, 0);
  const avgLoad = servers.length > 0
    ? Math.round(servers.reduce((sum, s) => sum + s.load, 0) / servers.length)
    : 0;

  if (loading) {
    return (
      <div className="aui-page servers-page">
        <button type="button" className="aui-back" onClick={() => navigate('/dashboard/admin')}>
          ← {t('systemAdmin.back', 'Back')}
        </button>
        <div className="aui-empty">
          <div className="aui-spinner" />
          <p>{t('systemAdmin.servers.loading', 'Loading servers…')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="aui-page servers-page">
      <button type="button" className="aui-back" onClick={() => navigate('/dashboard/admin')}>
        ← {t('systemAdmin.back', 'Back')}
      </button>

      <div className="aui-hero">
        <div>
          <p className="aui-kicker">Infrastructure</p>
          <h1>{t('systemAdmin.servers.title', 'Servers')}</h1>
          <p className="aui-lead">
            {t('systemAdmin.servers.subtitle', 'Deploy edges over SSH, approve nodes, set optional failover, manage register keys.')}
          </p>
        </div>
        <div className="aui-hero-actions">
          <button type="button" className="aui-btn ghost" onClick={handleAddLocation}>
            Locations ({locations.length})
          </button>
          {pendingNodes.length > 0 && (
            <button type="button" className="aui-btn ghost" onClick={() => setShowPendingModal(true)}>
              Pending ({pendingNodes.length})
            </button>
          )}
          <button type="button" className="aui-btn primary" onClick={handleOpenDeployModal}>
            {t('systemAdmin.servers.addServer', 'Deploy server')}
          </button>
        </div>
      </div>

      {error && (
        <div className="sv-banner error">
          <span>{error}</span>
          <button type="button" className="aui-btn ghost" onClick={fetchServers}>
            {t('systemAdmin.servers.retry', 'Retry')}
          </button>
        </div>
      )}

      <div className="aui-stats">
        <article className="aui-stat">
          <span>{t('systemAdmin.servers.stats.totalServers', 'Total')}</span>
          <strong>{totalServers}</strong>
        </article>
        <article className="aui-stat">
          <span>{t('systemAdmin.servers.stats.onlineServers', 'Online')}</span>
          <strong>{onlineServers}</strong>
        </article>
        <article className="aui-stat">
          <span>{t('systemAdmin.servers.stats.activeUsers', 'Active users')}</span>
          <strong>{totalUsers}</strong>
        </article>
        <article className="aui-stat">
          <span>{t('systemAdmin.servers.stats.avgLoad', 'Avg load')}</span>
          <strong>{avgLoad}%</strong>
        </article>
      </div>

      <div className="aui-toolbar">
        <div className="sv-filter-group">
          <label htmlFor="sv-country-filter">{t('systemAdmin.servers.filter.label', 'Country')}</label>
          <select
            id="sv-country-filter"
            className="aui-search"
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
          >
            <option value="all">{t('systemAdmin.servers.filter.all', 'All')}</option>
            {countries.filter(c => c !== 'all').map(country => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
        </div>
      </div>

      {filteredServers.length === 0 ? (
        <div className="aui-empty aui-panel">
          <h3>{t('systemAdmin.servers.empty.title', 'No servers yet')}</h3>
          <p>{t('systemAdmin.servers.empty.description', 'Deploy a server over SSH to register an agent here.')}</p>
        </div>
      ) : (
        <div className="sv-grid">
          {filteredServers.map(server => {
            const statusInfo = getStatusChip(server.status);
            const isPending = server.agentStatus === 'pending_approval' || !server.isApproved;
            return (
              <article key={server.id} className={`aui-card sv-card ${isPending ? 'is-pending' : ''}`}>
                <div className="sv-card-top">
                  <div>
                    <h3>
                      <span className="sv-flag">{server.flagEmoji}</span>
                      {server.name}
                    </h3>
                    <p>{server.city}, {server.country}</p>
                  </div>
                  <span className={`aui-chip ${statusInfo.cls}`}>{statusInfo.text}</span>
                </div>

                <div className="sv-meta">
                  <span>{server.ip}:{server.port || '51820'}</span>
                  {server.agentStatus && (
                    <span className={`aui-chip ${
                      server.agentStatus === 'connected' ? 'ok'
                        : server.agentStatus === 'pending_approval' ? 'warn' : 'danger'
                    }`}>
                      {server.agentStatus === 'connected' && t('systemAdmin.servers.agentStatus.connected', 'Connected')}
                      {server.agentStatus === 'disconnected' && t('systemAdmin.servers.agentStatus.disconnected', 'Disconnected')}
                      {server.agentStatus === 'pending_approval' && t('systemAdmin.servers.agentStatus.pending', 'Pending')}
                      {!['connected', 'disconnected', 'pending_approval'].includes(server.agentStatus) && server.agentStatus}
                    </span>
                  )}
                  {!isPending && (
                    <span className={`aui-chip ${server.isVisible ? 'signal' : ''}`}>
                      {server.isVisible ? 'Visible' : 'Hidden'}
                    </span>
                  )}
                  {server.fallbackNodeId ? (
                    <span className="aui-chip signal">Fallback #{server.fallbackNodeId}</span>
                  ) : null}
                  {isPending && <span className="aui-chip warn">Not approved</span>}
                </div>

                <div className="sv-metrics">
                  <div>
                    <div className="sv-metric-label">{t('systemAdmin.servers.card.load', 'Load')}</div>
                    <div className="sv-load-track">
                      <div
                        className="sv-load-fill"
                        style={{ width: `${server.load}%`, background: getLoadColor(server.load) }}
                      />
                    </div>
                    <div className="sv-metric-value" style={{ color: getLoadColor(server.load) }}>
                      {server.load}%
                    </div>
                  </div>
                  <div>
                    <div className="sv-metric-label">{t('systemAdmin.servers.card.users', 'Users')}</div>
                    <div className="sv-metric-value">{server.users} / {server.capacity}</div>
                  </div>
                </div>

                <div className="aui-row-actions">
                  {isPending ? (
                    <>
                      <button type="button" onClick={() => handleApproveNode(server.id)}>
                        {t('systemAdmin.servers.card.approve', 'Approve')}
                      </button>
                      <button type="button" className="danger" onClick={() => handleRejectClick(server)}>
                        {t('systemAdmin.servers.card.reject', 'Reject')}
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" onClick={() => handleToggleVisibility(server.id)}>
                        {server.isVisible ? 'Hide' : 'Show'}
                      </button>
                      <button type="button" onClick={() => handleToggleNode(server)}>
                        {server.status === 'online' ? 'Disable' : 'Enable'}
                      </button>
                      <button type="button" onClick={() => handleRestartNode(server)}>
                        Restart agent
                      </button>
                      <button type="button" onClick={() => handleSettingsClick(server)}>
                        {t('systemAdmin.servers.card.settings', 'Settings')}
                      </button>
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {showRejectModal && (
        <div className="aui-modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="aui-modal" onClick={e => e.stopPropagation()}>
            <h2>Reject server</h2>
            <p className="sv-modal-text">
              Reject <strong>{selectedNode?.name || `Node #${selectedNode?.id}`}</strong>? This cannot be undone.
            </p>
            <div className="aui-form-actions">
              <button type="button" className="aui-btn ghost" onClick={() => setShowRejectModal(false)}>Cancel</button>
              <button type="button" className="aui-btn danger" onClick={handleRejectConfirm}>Reject</button>
            </div>
          </div>
        </div>
      )}

      {showEditPendingModal && nodeToApprove && (
        <div className="aui-modal-overlay" onClick={() => setShowEditPendingModal(false)}>
          <div className="aui-modal" onClick={e => e.stopPropagation()}>
            <h2>{t('systemAdmin.servers.editBeforeApprove', 'Configure before approve')}</h2>
            <p className="sv-modal-text">
              {nodeToApprove.name || `Node #${nodeToApprove.id}`} · {nodeToApprove.ip_address || nodeToApprove.ip}:{nodeToApprove.endpoint_port || nodeToApprove.port}
            </p>
            <div className="aui-form">
              <label>
                {t('systemAdmin.servers.editPending.name', 'Server name')}
                <input
                  type="text"
                  value={editPendingForm.name}
                  onChange={e => setEditPendingForm({ ...editPendingForm, name: e.target.value })}
                  placeholder={nodeToApprove.name || 'Enter server name'}
                />
              </label>
              <label>
                {t('systemAdmin.servers.editPending.location', 'Location')} *
                <select
                  value={editPendingForm.location_id || ''}
                  onChange={e => setEditPendingForm({
                    ...editPendingForm,
                    location_id: e.target.value ? parseInt(e.target.value, 10) : null
                  })}
                  required
                >
                  <option value="">{t('systemAdmin.servers.editPending.selectLocation', 'Select a location')}</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>
                      {loc.flag_emoji} {loc.city}, {loc.country || loc.country_name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Fallback server (optional)
                <select
                  value={approveFallbackId || 0}
                  onChange={e => setApproveFallbackId(parseInt(e.target.value, 10) || 0)}
                >
                  <option value={0}>None — clients drop if this node dies</option>
                  {servers
                    .filter(s => s.id !== nodeToApprove.id && s.isApproved)
                    .map(s => (
                      <option key={s.id} value={s.id}>
                        #{s.id} {s.name} ({s.city})
                      </option>
                    ))}
                </select>
              </label>
              <p className="sv-help">
                {t('systemAdmin.servers.editPending.visibilityNote', 'Server will be hidden from users after approval. You can make it visible later.')}
              </p>
            </div>
            <div className="aui-form-actions">
              <button type="button" className="aui-btn ghost" onClick={() => setShowEditPendingModal(false)}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                className="aui-btn primary"
                onClick={handleEditPendingSubmit}
                disabled={!editPendingForm.location_id}
              >
                {t('systemAdmin.servers.editPending.approveBtn', 'Approve & save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPendingModal && (
        <div className="aui-modal-overlay" onClick={() => setShowPendingModal(false)}>
          <div className="aui-modal wide" onClick={e => e.stopPropagation()}>
            <h2>{t('systemAdmin.servers.pendingModal.title', 'Pending servers')}</h2>
            {pendingModalMessage && (
              <div className={`sv-banner ${pendingModalMessage.type}`}>
                {pendingModalMessage.text}
              </div>
            )}
            {pendingNodes.length === 0 ? (
              <p className="sv-modal-text">{t('systemAdmin.servers.pendingModal.noPending', 'No pending servers')}</p>
            ) : (
              <div className="sv-pending-list">
                {pendingNodes.map(node => (
                  <div key={node.id} className="sv-pending-item">
                    <div>
                      <strong>{node.name || `Node #${node.id}`}</strong>
                      <span>{node.ip_address}:{node.endpoint_port}</span>
                      <code>{node.system_uuid}</code>
                    </div>
                    <div className="aui-row-actions">
                      <button type="button" onClick={() => handleApproveNode(node.id)}>
                        {t('systemAdmin.servers.pendingModal.approve', 'Approve')}
                      </button>
                      <button type="button" className="danger" onClick={() => handleRejectClick(node)}>
                        {t('systemAdmin.servers.pendingModal.reject', 'Reject')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="aui-form-actions">
              <button type="button" className="aui-btn ghost" onClick={() => setShowPendingModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showLocationModal && (
        <div className="aui-modal-overlay" onClick={() => setShowLocationModal(false)}>
          <div className="aui-modal wide" onClick={e => e.stopPropagation()}>
            <h2>{selectedLocation ? 'Edit location' : 'New location'}</h2>
            <div className="aui-form">
              <label>
                Country name
                <input
                  type="text"
                  value={locationForm.country_name}
                  onChange={e => setLocationForm({ ...locationForm, country_name: e.target.value })}
                  placeholder="Turkey"
                />
              </label>
              <div className="sv-form-row">
                <label>
                  Country code
                  <input
                    type="text"
                    value={locationForm.country_code}
                    onChange={e => handleCountryCodeChange(e.target.value)}
                    placeholder="TR"
                    maxLength={3}
                  />
                </label>
                <label>
                  Flag
                  <div className="sv-flag-input">
                    <input
                      type="text"
                      value={locationForm.flag_emoji}
                      onChange={e => setLocationForm({ ...locationForm, flag_emoji: e.target.value })}
                      placeholder="🇹🇷"
                    />
                    <button type="button" className="aui-btn ghost" onClick={() => setShowFlagPicker(!showFlagPicker)}>
                      Pick
                    </button>
                  </div>
                </label>
              </div>
              {showFlagPicker && (
                <div className="sv-flag-picker">
                  {POPULAR_FLAGS.map(flag => (
                    <button
                      key={flag}
                      type="button"
                      className={locationForm.flag_emoji === flag ? 'is-selected' : ''}
                      onClick={() => handleFlagSelect(flag)}
                    >
                      {flag}
                    </button>
                  ))}
                </div>
              )}
              <label>
                City
                <input
                  type="text"
                  value={locationForm.city}
                  onChange={e => setLocationForm({ ...locationForm, city: e.target.value })}
                  placeholder="Istanbul"
                />
              </label>

              <div className="sv-locations-list">
                <h3>Existing locations</h3>
                {locations.map(loc => (
                  <div key={loc.id} className="sv-location-item">
                    <span>{loc.flag_emoji} {loc.city}, {loc.country_name}</span>
                    <div className="aui-row-actions">
                      <button type="button" onClick={() => handleEditLocation(loc)}>Edit</button>
                      <button type="button" className="danger" onClick={() => handleDeleteLocation(loc.id)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="aui-form-actions">
              <button type="button" className="aui-btn ghost" onClick={() => setShowLocationModal(false)}>Cancel</button>
              <button type="button" className="aui-btn primary" onClick={handleLocationSave}>
                {selectedLocation ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSettingsModal && selectedNode && (
        <div className="aui-modal-overlay" onClick={() => setShowSettingsModal(false)}>
          <div className="aui-modal" onClick={e => e.stopPropagation()}>
            <h2>Server settings</h2>
            <div className="aui-form">
              <label>
                Server name
                <input
                  type="text"
                  value={settingsForm.name}
                  onChange={e => setSettingsForm({ ...settingsForm, name: e.target.value })}
                />
              </label>
              <label>
                Location
                <select
                  value={settingsForm.location_id}
                  onChange={e => setSettingsForm({ ...settingsForm, location_id: parseInt(e.target.value, 10) })}
                >
                  <option value={0}>Select…</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>
                      {loc.flag_emoji} {loc.city}, {loc.country_name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Fallback server (optional)
                <select
                  value={settingsForm.fallback_node_id || 0}
                  onChange={e => setSettingsForm({
                    ...settingsForm,
                    fallback_node_id: parseInt(e.target.value, 10) || 0
                  })}
                >
                  <option value={0}>None — clients drop if this node dies</option>
                  {servers
                    .filter(s => s.id !== selectedNode.id && s.isApproved)
                    .map(s => (
                      <option key={s.id} value={s.id}>
                        #{s.id} {s.name} ({s.city})
                      </option>
                    ))}
                </select>
              </label>
              <div>
                <span className="sv-field-label">Access (license types)</span>
                <div className="sv-license-checks">
                  {LICENSE_TYPES.map(type => (
                    <label key={type} className="sv-check">
                      <input
                        type="checkbox"
                        checked={settingsForm.license_types.includes(type)}
                        onChange={() => handleLicenseToggle(type)}
                      />
                      <span className={`sv-license-badge ${type}`}>{type}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="sv-node-info">
                <p><strong>IP:</strong> {selectedNode.ip}:{selectedNode.port}</p>
                <p><strong>Agent:</strong> {selectedNode.agentStatus || '—'}</p>
              </div>
              <div className="sv-danger-zone">
                <h3>Danger zone</h3>
                <p>Deleting a server permanently removes its peers and related data.</p>
                <button type="button" className="aui-btn danger" onClick={handleDeleteServerClick}>
                  Delete this server
                </button>
              </div>
            </div>
            <div className="aui-form-actions">
              <button type="button" className="aui-btn ghost" onClick={() => setShowSettingsModal(false)}>Cancel</button>
              <button type="button" className="aui-btn primary" onClick={handleSettingsSave}>Save</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteServerModal && selectedNode && (
        <div className="aui-modal-overlay" onClick={() => setShowDeleteServerModal(false)}>
          <div className="aui-modal" onClick={e => e.stopPropagation()}>
            <h2>Delete server</h2>
            <p className="sv-modal-text">
              Permanently delete <strong>{selectedNode.name}</strong>? Type the server name to confirm.
            </p>
            <div className="aui-form">
              <label>
                Confirmation
                <input
                  type="text"
                  value={deleteConfirmName}
                  onChange={e => setDeleteConfirmName(e.target.value)}
                  placeholder={selectedNode.name}
                />
              </label>
            </div>
            <div className="aui-form-actions">
              <button type="button" className="aui-btn ghost" onClick={() => setShowDeleteServerModal(false)}>Cancel</button>
              <button
                type="button"
                className="aui-btn danger"
                onClick={handleDeleteServerConfirm}
                disabled={deleteConfirmName !== selectedNode.name}
              >
                Delete server
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeployModal && (
        <div className="aui-modal-overlay" onClick={() => setShowDeployModal(false)}>
          <div className="aui-modal wide" onClick={e => e.stopPropagation()}>
            <h2>Deploy edge server</h2>
            <p className="sv-modal-text">
              Center SSHs into the host, installs the agent binary, and registers it. Provide password and/or private key.
            </p>
            <div className="aui-form">
              <label>
                Description
                <input
                  type="text"
                  value={deployForm.description}
                  onChange={e => setDeployForm({ ...deployForm, description: e.target.value })}
                  placeholder="Germany Server 1"
                />
              </label>
              <div className="sv-form-row">
                <label style={{ flex: 2 }}>
                  SSH host / IP
                  <input
                    type="text"
                    value={deployForm.ssh_host}
                    onChange={e => setDeployForm({ ...deployForm, ssh_host: e.target.value })}
                    placeholder="203.0.113.10"
                  />
                </label>
                <label style={{ flex: 1 }}>
                  Port
                  <input
                    type="number"
                    value={deployForm.ssh_port}
                    onChange={e => setDeployForm({ ...deployForm, ssh_port: parseInt(e.target.value, 10) || 22 })}
                  />
                </label>
                <label style={{ flex: 1 }}>
                  User
                  <input
                    type="text"
                    value={deployForm.ssh_user}
                    onChange={e => setDeployForm({ ...deployForm, ssh_user: e.target.value })}
                  />
                </label>
              </div>
              <label>
                SSH password (optional if key provided)
                <input
                  type="password"
                  value={deployForm.ssh_password}
                  onChange={e => setDeployForm({ ...deployForm, ssh_password: e.target.value })}
                  autoComplete="new-password"
                />
              </label>
              <label>
                SSH private key (optional if password provided)
                <textarea
                  rows={5}
                  value={deployForm.ssh_private_key}
                  onChange={e => setDeployForm({ ...deployForm, ssh_private_key: e.target.value })}
                  placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
                />
              </label>
              <label>
                Install token validity (days)
                <input
                  type="number"
                  min={1}
                  value={deployForm.expires_in_days}
                  onChange={e => setDeployForm({
                    ...deployForm,
                    expires_in_days: parseInt(e.target.value, 10) || 30
                  })}
                />
              </label>
              <label className="sv-check">
                <input
                  type="checkbox"
                  checked={deployForm.deploy_now}
                  onChange={e => setDeployForm({ ...deployForm, deploy_now: e.target.checked })}
                />
                <span>Start install immediately</span>
              </label>
            </div>
            <div className="aui-form-actions">
              <button
                type="button"
                className="aui-btn ghost"
                onClick={() => {
                  setShowDeployModal(false);
                  handleOpenRegisterKeysModal();
                }}
              >
                Manual install token
              </button>
              <button type="button" className="aui-btn ghost" onClick={() => setShowDeployModal(false)}>Close</button>
              <button type="button" className="aui-btn primary" disabled={deploying} onClick={handleDeployServer}>
                {deploying ? 'Deploying…' : 'Deploy'}
              </button>
            </div>

            <div className="sv-keys-toolbar" style={{ marginTop: '1.25rem' }}>
              <h3>Provisioned hosts ({edgeHosts.length})</h3>
              <button type="button" className="aui-btn ghost" onClick={fetchEdgeHosts}>Refresh</button>
            </div>
            {edgeHosts.length === 0 ? (
              <div className="aui-empty"><p>No edge hosts yet</p></div>
            ) : (
              <div className="aui-panel">
                <table className="aui-table">
                  <thead>
                    <tr>
                      <th>Host</th>
                      <th>Auth</th>
                      <th>Status</th>
                      <th>Node</th>
                      <th>Error</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {edgeHosts.map(h => (
                      <tr key={h.id}>
                        <td>{h.ssh_user}@{h.ssh_host}:{h.ssh_port}</td>
                        <td>
                          {h.has_password ? 'password ' : ''}
                          {h.has_private_key ? 'key' : ''}
                        </td>
                        <td><span className="aui-chip">{h.provision_status}</span></td>
                        <td>{h.node_id || '—'}</td>
                        <td style={{ maxWidth: 220, fontSize: '0.78rem' }}>
                          {h.last_provision_error || '—'}
                        </td>
                        <td>
                          <div className="aui-row-actions">
                            <button
                              type="button"
                              className="aui-btn ghost"
                              onClick={() => handleRedeployHost(h.id)}
                            >
                              Redeploy
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {showRegisterKeysModal && (
        <div className="aui-modal-overlay" onClick={() => setShowRegisterKeysModal(false)}>
          <div className="aui-modal wide" onClick={e => e.stopPropagation()}>
            <h2>Manual install tokens</h2>
            <p className="sv-modal-text">
              Only needed if you cannot SSH-deploy from the center. Prefer Deploy server for normal installs.
            </p>
            <div className="aui-form">
              <div className="sv-form-row">
                <label style={{ flex: 2 }}>
                  Description
                  <input
                    type="text"
                    value={newKeyForm.description}
                    onChange={e => setNewKeyForm({ ...newKeyForm, description: e.target.value })}
                    placeholder="e.g. Germany Server 1"
                  />
                </label>
                <label style={{ flex: 1 }}>
                  Validity (days)
                  <input
                    type="number"
                    value={newKeyForm.expires_in_days}
                    onChange={e => setNewKeyForm({
                      ...newKeyForm,
                      expires_in_days: parseInt(e.target.value, 10) || 0
                    })}
                    min="0"
                  />
                </label>
              </div>
              <div className="aui-form-actions" style={{ marginTop: 0 }}>
                <button type="button" className="aui-btn primary" onClick={handleCreateRegisterKey}>
                  Create key
                </button>
              </div>
            </div>

            <div className="sv-keys-toolbar">
              <h3>Existing keys ({filteredAndSortedKeys.length})</h3>
              <div className="sv-keys-controls">
                <input
                  className="aui-search"
                  type="text"
                  placeholder="Search key or description…"
                  value={keySearchQuery}
                  onChange={e => {
                    setKeySearchQuery(e.target.value);
                    setKeyCurrentPage(1);
                  }}
                />
                <select
                  className="aui-search"
                  value={keySortOrder}
                  onChange={e => setKeySortOrder(e.target.value)}
                >
                  <option value="date_desc">Newest</option>
                  <option value="date_asc">Oldest</option>
                  <option value="key_asc">Key (A–Z)</option>
                </select>
              </div>
            </div>

            {filteredAndSortedKeys.length === 0 ? (
              <div className="aui-empty">
                <p>{keySearchQuery ? 'No results found' : 'No register keys yet'}</p>
              </div>
            ) : (
              <>
                <div className="aui-panel">
                  <table className="aui-table">
                    <thead>
                      <tr>
                        <th>Key</th>
                        <th>Description</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Expires</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedKeys.map(key => (
                        <tr key={key.id}>
                          <td>
                            <div className="sv-key-cell">
                              <code>{key.register_key.substring(0, 20)}…</code>
                              <button
                                type="button"
                                className="aui-btn ghost"
                                onClick={() => {
                                  navigator.clipboard.writeText(key.register_key);
                                  showSuccess('Key copied!');
                                }}
                              >
                                Copy
                              </button>
                            </div>
                          </td>
                          <td>{key.description || '—'}</td>
                          <td>
                            {key.is_revoked ? (
                              <span className="aui-chip danger">Revoked</span>
                            ) : key.is_used ? (
                              <span className="aui-chip">Used</span>
                            ) : (
                              <span className="aui-chip ok">Available</span>
                            )}
                          </td>
                          <td>{new Date(key.created_at).toLocaleDateString('en-US')}</td>
                          <td>
                            {key.expires_at
                              ? new Date(key.expires_at).toLocaleDateString('en-US')
                              : 'Never'}
                          </td>
                          <td>
                            {!key.is_used && !key.is_revoked && (
                              <div className="aui-row-actions">
                                <button type="button" className="danger" onClick={() => handleRevokeClick(key.id)}>
                                  Revoke
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalKeyPages > 1 && (
                  <div className="sv-pagination">
                    <button
                      type="button"
                      className="aui-btn ghost"
                      disabled={keyCurrentPage === 1}
                      onClick={() => setKeyCurrentPage(p => p - 1)}
                    >
                      Previous
                    </button>
                    <span>Page {keyCurrentPage} / {totalKeyPages}</span>
                    <button
                      type="button"
                      className="aui-btn ghost"
                      disabled={keyCurrentPage === totalKeyPages}
                      onClick={() => setKeyCurrentPage(p => p + 1)}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}

            <div className="aui-form-actions">
              <button type="button" className="aui-btn ghost" onClick={() => setShowRegisterKeysModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showRevokeConfirmModal && (
        <div className="aui-modal-overlay" onClick={() => setShowRevokeConfirmModal(false)}>
          <div className="aui-modal" onClick={e => e.stopPropagation()}>
            <h2>Revoke key</h2>
            <p className="sv-modal-text">
              Revoke this register key? It will no longer be usable.
            </p>
            <div className="aui-form-actions">
              <button type="button" className="aui-btn ghost" onClick={() => setShowRevokeConfirmModal(false)}>
                Cancel
              </button>
              <button type="button" className="aui-btn danger" onClick={handleRevokeConfirm}>
                Revoke
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Servers;
